import React, { useState, useEffect } from 'react';
import { 
  Users, LogIn, MessageCircle, UserPlus, 
  Clock, MapPin, Monitor, Smartphone, Globe,
  ChevronDown, ChevronUp, RefreshCw, Filter,
  Activity, TrendingUp, TrendingDown, AlertCircle, Eye
} from 'lucide-react';
import { getApiBaseUrl, safeParseJson } from '../../utils/apiBaseUrl';
import './RecentActivitiesAnalytics.css';

const RecentActivitiesAnalytics = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [recentLogins, setRecentLogins] = useState([]);
  const [summary, setSummary] = useState({});
  const [loginStats, setLoginStats] = useState({});
  const [expandedItems, setExpandedItems] = useState(new Set());

  const tabs = [
    { id: 'all', label: 'All Activities', icon: Activity },
    { id: 'logins', label: 'Recent Logins', icon: LogIn }
  ];

  const timeRanges = [
    { value: '1h', label: 'Last Hour' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' }
  ];

  const fetchRecentActivities = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/analytics/recent-activities?timeRange=${timeRange}&limit=50`);
      const data = await safeParseJson(response);
      if (data && data.success) {
        setActivities(data.activities);
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentLogins = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/analytics/recent-logins?timeRange=${timeRange}&limit=50`);
      const data = await safeParseJson(response);
      if (data && data.success) {
        setRecentLogins(data.logins);
        setLoginStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching recent logins:', error);
    }
  };


  useEffect(() => {
    fetchRecentActivities();
    fetchRecentLogins();
  }, [timeRange]);

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'login':
        return <LogIn size={16} className="activity-icon login" />;
      case 'chat_started':
        return <MessageCircle size={16} className="activity-icon chat" />;
      case 'registration':
        return <UserPlus size={16} className="activity-icon registration" />;
      default:
        return <Activity size={16} className="activity-icon default" />;
    }
  };

  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'login':
        return '#10b981';
      case 'chat_started':
        return '#8b5cf6';
      case 'registration':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <Monitor size={14} />;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone size={14} />;
    }
    return <Monitor size={14} />;
  };

  const renderActivityItem = (activity, index) => {
    const isExpanded = expandedItems.has(`${activity.activity_type}_${index}`);

    return (
      <div 
        key={`${activity.activity_type}_${index}`}
        className="activity-item"
        onClick={() => toggleExpanded(`${activity.activity_type}_${index}`)}
      >
        <div className="activity-icon">
          {getActivityIcon(activity.activity_type)}
        </div>
        <div className="activity-content">
          <div className="activity-description">
            <span className="user-name">{activity.user_name}</span> - {activity.description}
          </div>
          <div className="activity-user">
            {activity.user_type === 'authenticated' ? 'Authenticated User' : 'Anonymous User'}
            {activity.business_type && ` • ${activity.business_type}`}
          </div>
          <div className="activity-time">
            {activity.timeAgo}
          </div>
        </div>
        
        {isExpanded && (
          <div className="activity-details">
            <div className="detail-row">
              <strong>Email:</strong> {activity.email || 'N/A'}
            </div>
            {activity.ip_address && (
              <div className="detail-row">
                <strong>IP Address:</strong> {activity.ip_address}
              </div>
            )}
            {activity.user_agent && (
              <div className="detail-row">
                <strong>Device:</strong> 
                <span className="device-info">
                  {getDeviceIcon(activity.user_agent)}
                  <span className="user-agent-text">
                    {activity.user_agent.length > 50 
                      ? `${activity.user_agent.substring(0, 50)}...` 
                      : activity.user_agent
                    }
                  </span>
                </span>
              </div>
            )}
            {activity.session_id && (
              <div className="detail-row">
                <strong>Session ID:</strong> 
                <span className="session-id">{activity.session_id.substring(0, 8)}...</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStatsCards = () => {
    if (activeTab === 'all') {
      return (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <LogIn size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{summary.totalLogins || 0}</div>
            <div className="stat-title">Recent Logins</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <Eye size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{summary.totalChats || 0}</div>
            <div className="stat-title">Total Chats</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <MessageCircle size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{summary.totalChatSessions || 0}</div>
            <div className="stat-title">Chat Sessions</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <UserPlus size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{summary.totalRegistrations || 0}</div>
            <div className="stat-title">New Registrations</div>
          </div>
        </div>
      );
    } else if (activeTab === 'logins') {
      return (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <LogIn size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{loginStats.totalLogins || 0}</div>
            <div className="stat-title">Total Logins</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <Users size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{loginStats.uniqueUsers || 0}</div>
            <div className="stat-title">Unique Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <Activity size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{loginStats.currentlyOnline || 0}</div>
            <div className="stat-title">Currently Online</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-background"></div>
            <div className="stat-card-header">
              <div className="stat-icon">
                <div className="icon-glow"></div>
                <Clock size={20} className="icon" />
              </div>
            </div>
            <div className="stat-value">{loginStats.avgMinutesSinceLogin || 0}m</div>
            <div className="stat-title">Avg Time Since Login</div>
          </div>
        </div>
      );
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'logins':
        return recentLogins;
      default:
        return activities;
    }
  };

  return (
    <div className="recent-activities-analytics">
      <div className="analytics-header">
        <div className="header-left">
          <h2>Recent Activities Analytics</h2>
          <p>Monitor user interactions and system events in real-time</p>
        </div>
        <div className="header-right">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="date-select"
          >
            {timeRanges.map(range => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
          <button 
            onClick={() => {
              fetchRecentActivities();
              fetchRecentLogins();
            }}
            className="refresh-button"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'refreshing' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="tabs-container">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              <IconComponent size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {renderStatsCards()}

      <div className="recent-activity-section">
        <h3>
          {activeTab === 'all' ? 'All Recent Activities' : 'Recent Logins'}
        </h3>
        <div className="activity-list">
          {loading ? (
            <div className="loading-container">
              <RefreshCw size={24} className="loading-spinner" />
              <p>Loading activities...</p>
            </div>
          ) : getCurrentData().length === 0 ? (
            <div className="empty-state">
              <AlertCircle size={48} className="error-icon" />
              <p>No activities found for the selected time range</p>
            </div>
          ) : (
            getCurrentData().map((activity, index) => renderActivityItem(activity, index))
          )}
        </div>
      </div>
    </div>
  );
};

export default RecentActivitiesAnalytics;
